import React, { useEffect, useState, useMemo, useCallback } from "react";
import styled from "styled-components";
import CreateDocumentFormField from ".";
import SearchableSelect from "components/foundation/SearchableSelect/SearchableSelect";
import { ReferenceUserDto } from "apis/data-contracts";
import { MAX_REFERENCE_USERS } from "config/constant";
import ReferenceUserItem from "components/foundation/ReferenceUserItem/ReferenceUserItem";
import useRegularMembersQuery from "features/user/hooks/queries/useRegularMembersQuery";
import { SUPERADMIN_UUID } from "config/documentConstant";

interface ReferenceFieldProps {
  defaultReferenceUsers: ReferenceUserDto[];
  onChangeReferenceUsers: (referenceUsers: ReferenceUserDto[]) => void;
  isModal?: boolean;
  author: string;
  decisionManager?: string;
}

const ReferenceField = ({
  defaultReferenceUsers,
  onChangeReferenceUsers,
  isModal = false,
  author,
  decisionManager,
}: ReferenceFieldProps) => {
  // 정회원 목록 조회
  const { data: users } = useRegularMembersQuery({});

  // 전체 정회원 리스트 생성
  const userList = useMemo(() => {
    return (
      users
        // 참조자에서 작성자, 전결권자로 선택된 유저, 대표이사 제외
        .filter(
          (user) =>
            user.uuid !== author &&
            decisionManager !== user.uuid &&
            user.uuid !== SUPERADMIN_UUID,
        )
        .map((user) => ({
          label: user.name,
          value: user.uuid,
        })) || []
    );
  }, [users, decisionManager, author]);

  // 선택된 참조 유저 리스트
  const [selectedReferenceUsers, setSelectedReferenceUsers] = useState<
    ReferenceUserDto[]
  >([]);

  // 임시저장된 참조 유저 리스트 불러오기
  useEffect(() => {
    setSelectedReferenceUsers(defaultReferenceUsers);
  }, [defaultReferenceUsers]);

  // 선택된 유저 제외한 유저 리스트 : 참조 유저 중복 선택 방지
  const updatedUserList = useMemo(() => {
    return userList.filter(
      (user) =>
        !selectedReferenceUsers.some(
          (selectedUser) => selectedUser.uuid === user.value,
        ),
    );
  }, [userList, selectedReferenceUsers]);

  // 참조 유저 추가
  const onChangeSelectedUsers = useCallback(
    (value: string) => {
      if (!value) {
        return;
      }

      const selectedUser = userList.find((user) => user.value === value);
      if (selectedUser) {
        setSelectedReferenceUsers((prev) => {
          const newSelectedReferenceUsers = [
            ...prev,
            {
              uuid: selectedUser.value,
              name: selectedUser.label,
            },
          ];

          onChangeReferenceUsers(newSelectedReferenceUsers);
          return newSelectedReferenceUsers;
        });
      }
    },
    [userList, onChangeReferenceUsers],
  );

  // 참조 유저 삭제
  const onClickRemoveReferenceUser = useCallback(
    (uuid: string) => {
      setSelectedReferenceUsers((prev) => {
        const newSelectedReferenceUsers = prev.filter(
          (user) => user.uuid !== uuid,
        );

        onChangeReferenceUsers(newSelectedReferenceUsers);
        return newSelectedReferenceUsers;
      });
    },
    [onChangeReferenceUsers],
  );

  return (
    <>
      {isModal ? (
        <>
          {selectedReferenceUsers?.length > 0 &&
            selectedReferenceUsers.map((user) => (
              <ReferenceUserItem
                key={user.uuid}
                name={user.name}
                onClickRemove={() => onClickRemoveReferenceUser(user.uuid)}
              />
            ))}
          {selectedReferenceUsers?.length < MAX_REFERENCE_USERS && (
            <SelectStyle
              key={selectedReferenceUsers.length} // 참조 유저 선택할 때마다 컴포넌트 재생성
              placeholder="선택하세요."
              list={updatedUserList}
              onValueChange={onChangeSelectedUsers}
            />
          )}
        </>
      ) : (
        <>
          <WrapStyle>
            <CreateDocumentFormField
              label="참조지정"
              labelStyle={{
                lineHeight: "4.6rem",
              }}
              bodyStyle={{
                maxWidth: "unset",
                display: "flex",
                flexDirection: "column",
                gap: "1.25rem",
              }}
              style={{
                alignItems: "flex-start",
              }}
            >
              {selectedReferenceUsers?.length > 0 &&
                selectedReferenceUsers.map((user) => (
                  <ReferenceUserItem
                    key={user.uuid}
                    name={user.name}
                    onClickRemove={() => onClickRemoveReferenceUser(user.uuid)}
                  />
                ))}

              {selectedReferenceUsers?.length < MAX_REFERENCE_USERS && (
                <SelectStyle
                  key={selectedReferenceUsers.length} // 참조 유저 선택할 때마다 컴포넌트 재생성
                  placeholder="선택하세요."
                  list={updatedUserList}
                  onValueChange={onChangeSelectedUsers}
                />
              )}
            </CreateDocumentFormField>
          </WrapStyle>
        </>
      )}
    </>
  );
};

export default ReferenceField;

const WrapStyle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SelectStyle = styled(SearchableSelect)`
  width: 100%;
`;
